import { useApolloClient } from '@apollo/client/react';
import { ButtonToggle } from '../../../components/ButtonToggle';
import { FormikInput } from '../../../components/formik/FormInput';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { T, useT } from '../../../translation/src';
import { UserRole, useProjectAddGuestMutation, useUserAddToOrganisationMutation } from '../../../types/graphqlTypes';
import { FormikConfig, FormikHelpers } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { ProjectAccessPicker, ProjectAccessSelection } from '../../project/ProjectAccessPicker';

export const useValidationSchema = () => {
  const translations = useValidationTranslations();

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup.string().trim().email(translations.validEmail).required(translations.isRequired),
      }),
    [translations],
  );

  return schema;
};

interface FormValues {
  email: string;
}

const buttonToggleOptions = [
  { value: UserRole.Member, label: <T _str="member" swc /> },
  {
    value: UserRole.Admin,
    label: <T _str="admin" swc />,
  },
  { value: UserRole.Guest, label: <T _str="guest" swc /> },
];

const useTranslations = () => {
  const email = useT('email', { swc: true });
  return { email };
};
interface AddUserModalProps {
  onClose: () => void;
  open?: boolean;
}
const AddUserModal_: React.FC2<AddUserModalProps> = ({ onClose, open }) => {
  const validationSchema = useValidationSchema();
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const apolloClient = useApolloClient();
  const [addUser, { loading: addUserLoading }] = useUserAddToOrganisationMutation();
  const [addProjectGuest, { loading: addGuestLoading }] = useProjectAddGuestMutation();
  const [role, setRole] = useState<UserRole>(UserRole.Member);
  const [projectAccess, setProjectAccess] = useState<ProjectAccessSelection>(new Map());

  const onSuccess = useCallback(
    ({ helpers: { resetForm } }: { helpers: FormikHelpers<FormValues> }) => {
      onClose();
      // Because of transition
      setTimeout(() => {
        resetForm();
        setProjectAccess(new Map());
      }, 250);
    },
    [onClose],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      const email = values.email.trim().toLowerCase();
      // One organisation invite (the only mail), then project access for guests.
      await addUser({ variables: { organisationId, email, role } });
      if (role === UserRole.Guest) await Promise.all([...projectAccess].map(([projectId, level]) => addProjectGuest({ variables: { projectId, email, level } })));
      await apolloClient.refetchQueries({ include: ['users', 'currentUser', 'organisations'] });
      onSuccess({ helpers });
    },
    [addUser, addProjectGuest, apolloClient, onSuccess, organisationId, role, projectAccess],
  );

  const onChangeRole = useCallback((value: UserRole) => {
    setRole(value);
  }, []);

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: { email: '' },
      onSubmit,
      validationSchema,
    }),
    [onSubmit, validationSchema],
  );

  const description = useMemo(() => {
    if (role === UserRole.Admin) return <T _str="Admins have access to all projects and can manage your organisation, your organisation users, your subscription and payments." />;
    if (role === UserRole.Guest) return <T _str="Guests only see the projects you pick below, at the level you choose. You can change this later from the users page." />;
    return <T _str="Members have access to all projects." />;
  }, [role]);

  return (
    <CreateModal title={<T _str="add member" swc />} createButtonTitle={<T _str="send invitation" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={addUserLoading || addGuestLoading} panelClassName={role === UserRole.Guest ? 'w-full max-w-3xl' : undefined}>
      <ButtonToggle options={buttonToggleOptions} onChange={onChangeRole} value={role} />
      <div className="mt-2 mb-3 text-sm text-gray-400">{description}</div>
      <FormikInput name="email" label={translations.email} placeholder={translations.email} />
      {role === UserRole.Guest && (
        <div className="mt-4">
          <ProjectAccessPicker organisationId={organisationId} selection={projectAccess} onChange={setProjectAccess} active={!!open} />
        </div>
      )}
    </CreateModal>
  );
};

export const AddUserModal = memo(AddUserModal_);
